import React, { useEffect, useState } from 'react';
import {
    Modal,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    IconButton,
    Button
} from '@mui/material';
import { Icon } from "@iconify/react";
import closeIcon from '@iconify/icons-mdi/close-circle-outline';
import crownIcon from '@iconify/icons-mdi/crown';
import { getData } from '../../../webService/webservice';
import { DateTime } from 'luxon';
import { generateWalletPrintHTML } from '../../../utils/invoicePrintTemplates';

const CustomerDetailsPopup = ({ open, onClose, customer }) => {
    const [invoices, setInvoices] = useState([]);
    const [totalSpend, setTotalSpend] = useState(0);
    const [wallets, setWallets] = useState([]);

    const sessionToken = localStorage.getItem("Token");
    console.log("CUSTOMER OBJECT 👉", customer);

    const imageUrl = customer?.customerImageObject?.uri
        ? `${process.env.REACT_APP_API_URL}${customer.customerImageObject.uri}`
        : customer?.profileImage || "/assets/management-consulting.png";


    console.log("IMAGE URL 👉", imageUrl);
    useEffect(() => {
        if (open && customer?._id) {
            fetchCustomerHistory();
        }
    }, [open, customer?._id]);

    const fetchCustomerHistory = async () => {
        try {

            const res = await getData(
                "invoices/invoices/customer",
                {
                    customerId: customer._id,
                    limit: 100,
                    sort: JSON.stringify([["createdAt", -1]])
                },
                sessionToken
            );

            const fetchedInvoices = res?.data?.results || [];

            setInvoices(fetchedInvoices);

            // Calculate Total Spend
            const total = fetchedInvoices.reduce(
                (sum, inv) => sum + (Number(inv.grandTotal) || 0),
                0
            );

            setTotalSpend(total);

            // Fetch Customer Wallets
            try {
                const walletRes = await getData(`wallets/customer/all/${customer._id}`, {}, sessionToken);
                if (walletRes && walletRes.data) {
                    setWallets(Array.isArray(walletRes.data) ? walletRes.data : []);
                }
            } catch (wErr) {
                console.error("Failed to fetch customer wallets", wErr);
            }

        } catch (error) {
            console.error("Failed to fetch customer history", error);
        }
    };

    const handlePrintWallet = (w) => {
        if (!w) return;
        const walletData = { ...w, customerId: customer };
        const html = generateWalletPrintHTML(walletData);
        const win = window.open("", "WALLET_PRINT", "width=850,height=750");
        if (win) {
            win.document.open();
            win.document.write(html);
            win.document.close();
            win.onload = () => {
                setTimeout(() => {
                    win.focus();
                    win.print();
                }, 400);
            };
        }
    };


    const subs = customer?.subscriptionDetails || customer?.memberships || customer?.subscriptions || [];
    const now = new Date();

    const activeSub = subs.find(s => {
        if (!s.subscriptionStartDate || !s.subscriptionEndDate) return false;
        const startDate = new Date(s.subscriptionStartDate);
        const endDate = new Date(s.subscriptionEndDate);
        endDate.setHours(23, 59, 59, 999);
        const isActive = s.isActive === true ||
            String(s.isActive).toLowerCase() === "true" ||
            String(s.isActive).toLowerCase() === "active";
        return startDate <= now && endDate >= now && isActive;
    });

    const expiredSub = !activeSub ? [...subs].sort((a, b) => new Date(b.subscriptionEndDate) - new Date(a.subscriptionEndDate)).find(s => {
        if (!s.subscriptionEndDate) return false;
        const endDate = new Date(s.subscriptionEndDate);
        endDate.setHours(23, 59, 59, 999);
        return endDate < now;
    }) : null;

    const hasMembership = !!activeSub;
    const isExpired = !!expiredSub;
    const membershipName = activeSub?.subscriptionName || expiredSub?.subscriptionName || "No Membership";

    const cellStyle = {
        py: 1.2,
        px: { xs: 0.75, sm: 2 },
        fontSize: { xs: '10px', sm: '13px' },
        whiteSpace: 'nowrap'
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            slotProps={{
                backdrop: {
                    sx: {
                        backdropFilter: 'blur(4px)',
                        backgroundColor: 'rgba(0,0,0,0.4)'
                    }
                }
            }}
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: 600 },
                bgcolor: '#ffffff',
                background: hasMembership
                    ? 'linear-gradient(-45deg, #ffffff, #fffbeb, #f8fafc, #ffffff)'
                    : isExpired
                        ? 'linear-gradient(-45deg, #ffffff, #fef2f2, #f8fafc, #ffffff)'
                        : 'linear-gradient(-45deg, #ffffff, #f9f1f2, #f8fafc, #ffffff)',
                backgroundSize: '400% 400%',
                borderRadius: 4,
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                px: { xs: 2, sm: 4 },
                py: { xs: 2.5, sm: 4 },
                outline: 'none',
                overflow: 'hidden',
                '@keyframes fadeInUp': {
                    from: { opacity: 0, transform: 'translate(-50%, -45%) scale(0.95)' },
                    to: { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' }
                },
                '@keyframes gradientMove': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' }
                },
                animation: 'fadeInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards, gradientMove 15s ease infinite',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: hasMembership
                        ? 'linear-gradient(90deg, #d4af37, #b8860b)'
                        : isExpired
                            ? 'linear-gradient(90deg, #ef4444, #991b1b)'
                            : 'linear-gradient(90deg, #3C0008, #6d000e)',
                    zIndex: 1
                }
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', position: 'relative', zIndex: 2 }}>
                    <Typography variant="h6" component="h2" fontWeight="800" sx={{ color: '#1e293b' }}>
                        Customer Profile
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: '#64748b', '&:hover': { color: '#3C0008', transform: 'rotate(90deg)' }, transition: 'all 0.3s' }}>
                        <Icon icon={closeIcon} width={24} height={24} />
                    </IconButton>
                </div>

                <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: { xs: 4, sm: 4 } }}>
                    {/* Profile Picture with Dynamic Border and Crown */}
                    <Box sx={{ position: 'relative', mt: { xs: 2, sm: 0 } }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -35,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10,
                                '@keyframes crownPremium': {
                                    '0%': { transform: 'translateX(-50%) translateY(0) rotate(0deg)' },
                                    '25%': { transform: 'translateX(-50%) translateY(-10px) rotate(-8deg)' },
                                    '50%': { transform: 'translateX(-50%) translateY(-15px) rotate(0deg)' },
                                    '75%': { transform: 'translateX(-50%) translateY(-10px) rotate(8deg)' },
                                    '100%': { transform: 'translateX(-50%) translateY(0) rotate(0deg)' }
                                },
                                '@keyframes shieldProtect': {
                                    '0%, 100%': { transform: 'translateX(-50%) scale(1) rotate(0deg)' },
                                    '50%': { transform: 'translateX(-50%) scale(1.15) rotate(5deg)' }
                                },
                                '@keyframes iconShine': {
                                    '0%, 100%': { filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3)) brightness(1)' },
                                    '50%': {
                                        filter: hasMembership
                                            ? 'drop-shadow(0 8px 25px rgba(212, 175, 55, 0.9)) brightness(1.4) contrast(1.1)'
                                            : isExpired
                                                ? 'drop-shadow(0 8px 25px rgba(239, 68, 68, 0.7)) brightness(1.2) contrast(1.1)'
                                                : 'drop-shadow(0 8px 25px rgba(60, 0, 8, 0.7)) brightness(1.2) contrast(1.1)'
                                    }
                                },
                                animation: `${hasMembership ? 'crownPremium 4s ease-in-out infinite' : 'shieldProtect 3s ease-in-out infinite'}, iconShine 3s ease-in-out infinite`
                            }}
                        >
                            <Icon
                                icon={hasMembership ? "twemoji:crown" : isExpired ? "twemoji:alarm-clock" : "twemoji:shield"}
                                width={hasMembership ? 55 : 45}
                                height={hasMembership ? 55 : 45}
                            />
                        </Box>
                        <Box
                            component="img"
                            src={imageUrl}   // ✅ USE SAFE URL
                            alt="Customer Profile"
                            sx={{
                                width: 100,
                                height: 100,
                                borderRadius: '50%',
                                border: `4px solid ${hasMembership ? '#22c55e' : isExpired ? '#ef4444' : '#64748b'}`,
                                boxDirection: 'alternate',
                                '@keyframes borderGlow': {
                                    '0%': { boxShadow: `0 0 0 0px ${hasMembership ? 'rgba(212, 175, 55, 0.4)' : isExpired ? 'rgba(239, 68, 68, 0.4)' : 'rgba(100, 116, 139, 0.4)'}` },
                                    '70%': { boxShadow: `0 0 0 10px ${hasMembership ? 'rgba(212, 175, 55, 0)' : isExpired ? 'rgba(239, 68, 68, 0)' : 'rgba(100, 116, 139, 0)'}` },
                                    '100%': { boxShadow: `0 0 0 0px ${hasMembership ? 'rgba(212, 175, 55, 0)' : isExpired ? 'rgba(239, 68, 68, 0)' : 'rgba(100, 116, 139, 0)'}` }
                                },
                                animation: 'borderGlow 2s infinite',
                                objectFit: 'cover'
                            }}
                        />

                    </Box>

                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, auto)' },
                        gap: { xs: 2.5, sm: 4 },
                        width: '100%',
                        justifyContent: { xs: 'center', sm: 'flex-start' }
                    }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', sm: 'flex-start' }, textAlign: { xs: 'center', sm: 'left' } }}>
                            <Typography variant="subtitle2" color="textSecondary">Name</Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {customer?.name || customer?.label || "N/A"}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', sm: 'flex-start' }, textAlign: { xs: 'center', sm: 'left' } }}>
                            <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
                            <Typography variant="body1" fontWeight={600}>
                                {customer?.phoneNumber || customer?.phone || "N/A"}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', sm: 'flex-start' }, textAlign: { xs: 'center', sm: 'left' } }}>
                            <Typography variant="subtitle2" color="textSecondary">Membership</Typography>
                            <Box
                                sx={{
                                    display: 'inline-block',
                                    padding: '2px 12px',
                                    borderRadius: '20px',
                                    backgroundColor: hasMembership ? 'rgba(212, 175, 55, 0.1)' : isExpired ? 'rgba(239, 68, 68, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                                    border: `1px solid ${hasMembership ? '#d4af37' : isExpired ? '#ef4444' : '#64748b'}`,
                                    '@keyframes textPulse': {
                                        '0%': { opacity: 0.8 },
                                        '50%': { opacity: 1, transform: 'scale(1.05)' },
                                        '100%': { opacity: 0.8 }
                                    },
                                    animation: 'textPulse 2s infinite ease-in-out'
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    fontWeight={800}
                                    sx={{
                                        color: hasMembership ? "#b8860b" : isExpired ? "#ef4444" : "#64748b",
                                        fontSize: '0.875rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                    }}
                                >
                                    <Icon icon={hasMembership ? "mdi:check-decagram" : isExpired ? "mdi:clock-alert" : "mdi:alert-circle"} />
                                    {hasMembership ? `MEMBER (${membershipName})` : isExpired ? `EXPIRED (${membershipName})` : "NON-MEMBER"}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', sm: 'flex-start' }, textAlign: { xs: 'center', sm: 'left' } }}>
                            <Typography variant="subtitle2" color="textSecondary">Total Spend</Typography>
                            <Typography variant="body1" fontWeight={600} color="#3C0008">
                                ₹{totalSpend.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </Typography>
                        </Box>
                        {customer?.customerNote && (
                            <Box sx={{
                                gridColumn: { xs: 'span 2', sm: 'span 4' },
                                mt: 1.5,
                                p: 2,
                                bgcolor: '#fff7ed',
                                borderLeft: '5px solid #f97316',
                                borderRadius: 2.5,
                                boxShadow: '0 2px 8px rgba(249, 115, 22, 0.08)'
                            }}>
                                <Typography variant="subtitle2" sx={{ color: '#c2410c', fontWeight: 800, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Icon icon="mdi:note-text" width={22} height={22} />
                                    Customer Note
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#1e293b', fontWeight: 600, fontSize: '1.05rem', mt: 0.75, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                    {customer.customerNote}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* ✅ CUSTOMER WALLETS SECTION */}
                <Box sx={{ mb: 2.5, p: 2, bgcolor: '#f8fafc', borderRadius: 2.5, border: '1px solid #cbd5e1' }}>
                    <Typography variant="subtitle1" fontWeight="800" sx={{ mb: 1, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Icon icon="solar:wallet-money-bold-duotone" width={20} height={20} style={{ color: '#3C0008' }} />
                        Active Customer Wallets
                    </Typography>
                    {wallets.length === 0 ? (
                        <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', fontSize: '0.85rem' }}>
                            No active wallets found. You can purchase or recharge a wallet plan for this customer directly from the POS sidebar.
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                            {wallets.map(w => (
                                <Box key={w._id} sx={{
                                    bgcolor: w.type === 'Cash' ? '#fefce8' : '#eff6ff',
                                    border: `1px solid ${w.type === 'Cash' ? '#fef08a' : '#bfdbfe'}`,
                                    borderRadius: 2,
                                    p: 1.25,
                                    minWidth: '220px',
                                    flex: '1 1 calc(50% - 12px)',
                                    position: 'relative'
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: w.type === 'Cash' ? '#854d0e' : '#1e40af', fontSize: '0.85rem' }}>
                                            {w.type === 'Cash' ? '💰' : '🏷️'} {w.walletPackageId?.name || (w.type === 'Cash' ? 'Cash Wallet' : 'Service Wallet')}
                                        </Typography>
                                        <IconButton size="small" onClick={() => handlePrintWallet(w)} title="Print Wallet Receipt" sx={{ p: 0.25, color: '#0284c7' }}>
                                            <Icon icon="mdi:printer" width={16} />
                                        </IconButton>
                                    </Box>
                                    {w.type === 'Cash' ? (
                                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#16a34a', fontSize: '0.9rem', mt: 0.5 }}>
                                            Available Balance: ₹{w.balance ?? 0}
                                        </Typography>
                                    ) : (() => {
                                        const svcName = w.serviceId?.serviceName || 
                                            w.services?.map(s => s.serviceId?.serviceName || s.serviceName).filter(Boolean).join(', ') || 
                                            w.customWalletName || 
                                            w.walletPackageId?.name || 
                                            'Service';
                                        
                                        const totCount = w.totalCount ?? 
                                            (w.services && w.services.length > 0 
                                                ? w.services.reduce((sum, s) => sum + (s.totalCount || (s.baseCount || 0) + (s.additionalCount || 0)), 0) 
                                                : 0);
                                        
                                        const remCount = w.remainingCount ?? 
                                            (w.services && w.services.length > 0 
                                                ? w.services.reduce((sum, s) => sum + (s.remainingCount ?? 0), 0) 
                                                : 0);

                                        return (
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3a8a', fontSize: '0.85rem', mt: 0.5 }}>
                                                Service: <strong>{svcName}</strong> | Rem: <span style={{ color: '#16a34a', fontWeight: 800 }}>{remCount} / {totCount}</span>
                                            </Typography>
                                        );
                                    })()}
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.25 }}>
                                        Valid till: {new Date(w.expiryDate).toLocaleDateString('en-GB')}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>

                <Typography variant="subtitle1" fontWeight="800" sx={{ mb: 2, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon icon="mdi:history" />
                    Recent Transactions
                </Typography>

                <TableContainer
                    sx={{
                        maxHeight: 280,
                        overflowX: 'auto',
                        borderRadius: '12px',
                        border: '1px solid #e2e8f0',
                        boxShadow: 'none',
                        '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                        '&::-webkit-scrollbar-track': { background: '#f1f5f9' },
                        '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '10px' },
                        '&::-webkit-scrollbar-thumb:hover': { background: '#94a3b8' }
                    }}
                >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...cellStyle, bgcolor: '#3C0008 !important', color: '#ffffff', fontWeight: 'bold' }}>Date</TableCell>
                                <TableCell sx={{ ...cellStyle, bgcolor: '#3C0008 !important', color: '#ffffff', fontWeight: 'bold' }}>Invoice No</TableCell>
                                <TableCell sx={{ ...cellStyle, bgcolor: '#3C0008 !important', color: '#ffffff', fontWeight: 'bold' }} align="right">Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoices.length > 0 ? (
                                invoices.map((inv, index) => (
                                    <TableRow
                                        key={inv._id}
                                        sx={{
                                            '&:nth-of-type(odd)': { bgcolor: '#f8fafc' },
                                            '&:hover': { bgcolor: '#f1f5f9', cursor: 'default' },
                                            transition: 'background-color 0.2s'
                                        }}
                                    >
                                        <TableCell sx={{ ...cellStyle, color: '#475569', fontWeight: 500 }}>
                                            {DateTime.fromISO(inv.createdAt).toFormat("dd/MM/yyyy")}
                                        </TableCell>
                                        <TableCell sx={{ ...cellStyle, color: '#1e293b', fontWeight: 700, fontFamily: 'monospace' }}>
                                            {inv.invoiceNo}
                                        </TableCell>
                                        <TableCell align="right" sx={{ ...cellStyle, color: '#3C0008', fontWeight: 800 }}>
                                            ₹{Number(inv.grandTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={3} align="center" sx={{ py: 8, color: '#94a3b8' }}>
                                        <Icon icon="mdi:database-off-outline" width={40} />
                                        <Typography variant="body2" sx={{ mt: 1 }}>No transaction history found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button

                        onClick={onClose}
                        className='add-Product-button'
                    >
                        Continue
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default CustomerDetailsPopup;
